<template>
    <div class="container-fluid pt-2">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"> <!-- Added gap-2 for spacing -->
                <!-- {{ Group Name and Description Inputs }} -->
                <div class="d-flex flex-grow-1" style="min-width: 300px;"> <!-- Wrapper for inputs -->
                    <input type="text" v-model="templateName" placeholder="Nama Template" class="form-control form-control-lg me-2"> <!-- Template Name Input -->
                    <input type="text" v-model="templateDescription" placeholder="Deskripsi Singkat Template" class="form-control form-control-lg"> <!-- Template Description Input -->
                </div>
                <!-- Page Size & Orientation Selectors -->
                <div class="d-flex align-items-center"> <!-- Group selectors -->
                    <div class="me-3"> <!-- Margin between selectors -->
                        <label for="pageSizeSelect" class="form-label form-label-sm me-2">Page Size:</label>
                        <select id="pageSizeSelect" v-model="selectedPageSize" class="form-select form-select-sm" style="width: auto;">
                            <option value="A4">A4</option>
                            <option value="A5">A5</option>
                            <option value="Letter">US Letter</option>
                            <option value="None">None (Fluid)</option>
                        </select>
                    </div>
                    <div>
                        <label for="orientationSelect" class="form-label form-label-sm me-2">Orientation:</label>
                        <select id="orientationSelect" v-model="selectedOrientation" class="form-select form-select-sm" style="width: auto;" :disabled="selectedPageSize === 'None'"> <!-- Disable if size is None -->
                            <option value="Portrait">Portrait</option>
                            <option value="Landscape">Landscape</option>
                        </select>
                    </div>
                </div>
            </div>
            <!-- {{ Removed card-body for description }} -->
            <!-- <div class="card-body py-2">
                 <input type="text" v-model="templateDescription" placeholder="Deskripsi Singkat Template" class="form-control">
            </div> -->
            <div class="card-body">
                <!-- Wrapper div with the key -->
                <div :key="editorKey">
                    <!-- TinyMCE Editor Component -->
                    <Editor
                        v-model="htmlContent"
                        :api-key="tinymceApiKey"
                        :init="editorConfig"
                    />
                </div>
            </div>
             <!-- {{ Modified card-footer to show full HTML output }} -->
            <div class="card-footer">
                <div class="d-flex justify-content-end gap-2 mb-2"> <!-- Buttons at the top of footer -->
                    <button class="btn btn-secondary" @click="cancelEdit">Batal</button>
                    <button class="btn btn-primary" @click="saveTemplate">Simpan Template</button>
                </div>
                 <!-- {{ Section to display the full combined HTML output }} -->
                 <div v-if="!showRawHtml">
                     <h5>Full HTML Output:</h5>
                     <pre class="bg-light p-2 border rounded"><code>{{ fullHtmlOutput }}</code></pre>
                 </div>
                 <!-- {{ Removed separate head and body output sections }} -->
                 <div v-if="!showRawHtml">
                     <h5>Generated &lt;head&gt; Output:</h5>
                     <pre class="bg-light p-2 border rounded"><code>{{ generatedHeadContent }}</code></pre>
                 </div>
                 <div v-if="!showRawHtml" class="mt-3">
                     <h5>Raw Body HTML Output (Editor Content):</h5>
                     <pre class="bg-light p-2 border rounded"><code>{{ htmlContent }}</code></pre>
                 </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
// Import onMounted, useRoute, and useRouter
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router'; // Import useRouter
import Editor from '@tinymce/tinymce-vue';
import * as setting from "@/api/services/SettingRequest";
import {toast} from 'vue3-toastify';

// Get route and router objects
const route = useRoute();
const router = useRouter(); // Get router instance

// --- Add refs for template metadata ---
const templateId = ref<number | null>(null); // To store the ID if editing
const templateName = ref<string>('');
const templateDescription = ref<string>('');

// Reactive variable to hold the HTML string content
const htmlContent = ref<string>(''); // Initialize as empty, will be loaded or default
const showRawHtml = ref(true);
const tinymceApiKey = 'v7vo1wqjpc39mj540hiqlzg0z05feq103mhllqn8tomr0pzp';
const selectedPageSize = ref<string>('A4');
const selectedOrientation = ref<string>('Portrait');


// --- Define Page Size Dimensions (Portrait) ---
const pageDimensions = {
    A4: { width: '210mm', height: '295mm' },
    A5: { width: '148mm', height: '208mm' },
    Letter: { width: '215.9mm', height: '277.4mm' }
};


// --- Computed property for dynamic content_style ---
const pageContentStyle = computed(() => {
    const size = selectedPageSize.value;
    const orientation = selectedOrientation.value;

    if (size === 'None' || !(size in pageDimensions)) {
        // Style for no fixed page size (fluid)
        return `
            body {
                margin: 5px auto; /* Center the page */
                padding: 15px; /* Basic padding */
            }
            .mce-pagebreak { /* Still style page breaks if used */
                 border-top: 1px dashed #ccc;
                 margin-top: 15px;
                 padding-top: 15px;
                 cursor: default;
                 display: block;
                 page-break-before: always;
            }
        `;
    }

    let dims = { ...pageDimensions[size as keyof typeof pageDimensions] }; // Copy dimensions

    // Swap width and height for Landscape
    if (orientation === 'Landscape') {
        [dims.width, dims.height] = [dims.height, dims.width];
    }else{
        [dims.width, dims.height] = [dims.width, dims.height];
    }

    // Style for fixed page sizes (A4, A5, Letter)
    return `
        body {
            margin: 5px auto; /* Center the page */
            padding: 30px; /* Padding inside the page */
            width: ${dims.width}; /* Dynamic width */
            min-height: ${dims.height}; /* Dynamic height */
            box-shadow: 0 0 10px rgba(0,0,0,.1); /* Add a subtle shadow */
            box-sizing: border-box;
        }
        .mce-pagebreak {
            border-top: 1px dashed #ccc;
            margin-top: 15px;
            padding-top: 15px;
            cursor: default;
            display: block;
            page-break-before: always;
        }
    `;
});

// --- {{ Add computed property for generated head content }} ---
const generatedHeadContent = computed(() => {
    // Basic head structure
    let head = '<head>\n';
    head += '  <meta charset="UTF-8">\n';
    head += '  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n';
    // Add title if available (using templateName as an example)
    if (templateName.value) {
         head += `  <title>${templateName.value}</title>\n`;
    }
    // Include the content_style within <style> tags
    if (pageContentStyle.value) {
        head += '  <style>\n';
        head += pageContentStyle.value.trim().split('\n').map(line => `    ${line.trim()}`).join('\n'); // Indent styles
        head += '\n  </style>\n';
    }
    // Note: This doesn't include styles from content_css ('dark'/'default')
    // as those are internal to TinyMCE unless they are URLs.
    head += '</head>';
    return head;
});

// --- {{ Add computed property for full HTML output }} ---
const fullHtmlOutput = computed(() => {
    let fullHtml = '<!DOCTYPE html>\n';
    fullHtml += '<html lang="en">\n'; // Assuming English, adjust if needed
    fullHtml += generatedHeadContent.value; // Add the generated head
    fullHtml += '\n<body>\n';
    fullHtml += htmlContent.value; // Add the body content from the editor
    fullHtml += '\n</body>\n';
    fullHtml += '</html>';
    return fullHtml;
});


// --- Computed property for the entire editor configuration ---
const editorConfig = computed(() => {
    return {
        plugins: [  'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'image', 'link', 'lists', 'media', 'searchreplace', 'table', 'visualblocks', 'wordcount', 'preview', 'importcss', 'autosave', 'save', 'directionality', 'code', 'visualchars', 'fullscreen', 'pagebreak', 'nonbreaking', 'insertdatetime', 'advlist', 'help', 'quickbars', 'accordion'], // Consolidated plugins
        menubar: 'file edit view insert format tools table help',
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | align lineheight | tinycomments | checklist numlist bullist indent outdent | emoticons charmap | removeformat | code fullscreen preview | save print | pagebreak', // Ensure pagebreak is in toolbar
        toolbar_mode: 'sliding',
        height: 700, // Adjusted height
        skin: (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oxide-dark' : 'oxide'),
        content_css: (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default'),
        content_style: pageContentStyle.value, // Use the computed style
        pagebreak_split_block: true,
        visualblocks_default_state: true,
        end_container_on_empty_block: true,
        autosave_ask_before_unload: true,
        autosave_interval: '30s',
        autosave_prefix: '{path}{query}-{id}-',
        autosave_restore_when_empty: false,
        autosave_retention: '2m',
        image_advtab: true,
        importcss_append: true,
        table_styling_options: true,
        table_default_attributes: {
            border: '1'
        },
        table_default_styles: {
            'border-collapse': 'collapse',
            'width': '100%'
        },
        save_onsavecallback: (editor: any) => {
             console.log('TinyMCE Save button clicked, triggering custom save');
             saveTemplate(); // Call your custom save function
        }
    };
});


// --- Computed property for the editor key ---
const editorKey = computed(() => {
    // Create a unique key based on size and orientation
    // This forces re-render when either changes
    return `tinymce-editor-${selectedPageSize.value}-${selectedOrientation.value}`; // Made key slightly more descriptive
});


const formDesign = ref({})
// --- Placeholder function to fetch template data ---
// Replace this with your actual API call
async function fetchTemplateData(id: number) {
    // Simulate API call delay
    return await setting.postRequest("/template/data", {ID:id}).then((resp)=>{
        if(resp.status){
            formDesign.value = resp.data;
            templateName.value = resp.data.name;
            templateDescription.value = resp.data.description;
            htmlContent.value = resp.data.html_content;
            selectedPageSize.value = resp.data.page_size;
            selectedOrientation.value = resp.data.orientation;
        }
    })
}

// --- Load data on mount ---
onMounted(async () => {
    const idFromRoute = route.params.id;
    if (idFromRoute) {
        const id = Number(idFromRoute);
        if (!isNaN(id)) {
            templateId.value = id; // Store the ID
            try {
                 await fetchTemplateData(id);
            } catch (error) {
                console.error('Failed to load template data:', error);
                htmlContent.value = '<h1>Error loading template</h1>';
            }
        } else {
             console.warn('Invalid template ID in route:', idFromRoute);
             htmlContent.value = '<h1>New Template</h1><p>Start creating your template here.</p>'; // Default for new
        }
    } else {
        templateId.value = null;
        templateName.value = '';
        templateDescription.value = '';
        htmlContent.value = '<h1>New Template</h1><p>Start creating your template here.</p>'; // Default for new
        selectedPageSize.value = 'A4'; // Default size
        selectedOrientation.value = 'Portrait'; // Default orientation
    }
});


// --- {{ Add Save and Cancel Functions }} ---
const saveTemplate = async () => {
    const templateData = {
        id: templateId.value, // Will be null for new templates
        name: templateName.value,
        description: templateDescription.value,
        html_head: generatedHeadContent.value,
        html_content: htmlContent.value,
        page_size: selectedPageSize.value,
        orientation: selectedOrientation.value
    };

    try {
        Object.assign(formDesign.value, templateData);
        if (templateData.id) {
            await setting.postRequest("/template/update", formDesign.value).then((resp)=>{
                if(resp.status == 200){
                    toast.success('Template update successfully!');
                }
            })
        } else {
            await setting.postRequest("/template/create", formDesign.value).then((resp)=>{
                if(resp.status == 200){
                    toast.success('Template create successfully!');
                }
            })
        }
        router.push({ name: 'template-list' });
    } catch (error) {
        toast.error('Gagal menyimpan template. Silakan coba lagi.')
    }
};

const cancelEdit = () => {
    router.push({ name: 'template-list' });
};

</script>

<style>
/* TinyMCE styles are usually handled by its skins, but you can add overrides here if necessary */
.tox-tinymce {
    border: 1px solid #dee2e6; /* Example: Add border matching Bootstrap */
}
</style>
